export type PersistedUser = (
	| {
			email: string;
			emailConsent: boolean;
			sms: string;
			smsConsent: boolean;
	  }
	| { email: string; emailConsent: string }
) & {
	id?: string;
};

/**
 * Sets a value to session storage
 */
export function setSessionValue(key: string, value: string): void {
	if (!isSessionStorageAvailable()) return;

	sessionStorage.setItem(key, value);
}

/**
 * gets a value from session storage
 */
export function getSessionValue(key: string): string {
	if (!isSessionStorageAvailable()) return '';

	return sessionStorage.getItem(key);
}

/**
 * shortcut method to persist email/sms and consent to session storage
 * email/sms will NOT be updated if it is already set in the session
 * consent will be updated
 */
export function persistUserConsent(
	key: 'email' | 'sms',
	contact: string | undefined,
	consent: boolean
): void {
	if (!isSessionStorageAvailable()) return;

	if (!sessionStorage.getItem(key) && contact) {
		sessionStorage.setItem(key, contact);
	}

	sessionStorage.setItem(`${key}Consent`, `${consent}`);
}

/**
 * gets saved email and email consent
 */
export function getPersistedUser(): PersistedUser {
	if (!isSessionStorageAvailable()) return {} as PersistedUser;

	return {
		email: sessionStorage.getItem('email'),
		emailConsent: sessionStorage.getItem('emailConsent') === 'true',
		sms: sessionStorage.getItem('sms'),
		smsConsent: sessionStorage.getItem('smsConsent') === 'true',
	};
}

/**
 * Checks whether a value exists in the session but not what the value is in the session
 */
export function sessionHasValue(key: string): boolean {
	return !!sessionStorage.getItem(key);
}

/**
 * since this script will normally operate in cross-domain contexts, sessionStorage access may be restricted
 * this check allows us to gracefully degrade if sessionStorage is not available
 */
export function isSessionStorageAvailable(): boolean {
	try {
		sessionStorage.setItem('_ics_ping', `${Date.now()}`);
		sessionStorage.removeItem('_ics_ping');
		return true;
	} catch (e) {
		console.warn(
			"sessionStorage is not available so some features may not work.  If you are in a private browser, please use the browser in normal mode or enable 3rd party cookies in your browser's settings."
		);
		return false;
	}
}
